import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Grid,
  Group,
  Stack,
  Text,
  Button,
  SimpleGrid,
  Title,
  Rating,
  Timeline,
  Badge,
  Skeleton,
  Box,
  Tabs,
  SegmentedControl,
  Anchor,
  Space,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { TripStatus } from "@safiri/shared";
import dayjs from "dayjs";
import {
  IconArrowLeft,
  IconCheck,
  IconBan,
  IconPlayerPause,
  IconPlayerPlay,
  IconEdit,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { humanize } from "../../lib/labels";
import { driversApi } from "../../api/drivers";
import { rideTypesApi } from "../../api/rideTypes";
import { tripsApi } from "../../api/trips";
import { consoleApi } from "../../api/console";
import { KpiGroup, KpiCard } from "../../components/console/KpiCard";
import type {
  DriverAccountStatus,
  DriverDocumentType,
  DriverDocumentStatus,
  DocumentRejectionReason,
  DocDetail,
} from "../../types/domain";
import { PageHeader } from "../../components/common/PageHeader";
import { StatusBadge } from "../../components/common/StatusBadge";
import { ImageThumb } from "../../components/common/ImageThumb";
import { S3Avatar } from "../../components/common/S3Avatar";
import { QueryError } from "../../components/common/QueryError";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatPercent,
  parseBackendDate,
  en,
} from "../../lib/format";
import { VehicleIcon } from "../../components/brand/VehicleIcon";
import { errorMessage } from "../../lib/axios";
import {
  ApproveDriverModal,
  type LatraApprovalDetails,
} from "./ApproveDriverModal";
import { LicenseExpiryModal } from "./LicenseExpiryModal";
import { StatusChangeModal } from "./StatusChangeModal";
import {
  DriverEditModal,
  toDriverUpdateBody,
  type DriverEditValues,
} from "./DriverEditModal";
import { DriverFinanceTab } from "./DriverFinanceTab";
import { DriverReferralsTab } from "./DriverReferralsTab";
import { AccountTagsCard } from "../../components/tags/AccountTagsCard";
import { usePermission } from "../../auth/usePermission";
import {
  ReviewDocumentModal,
  type ReviewDocumentTarget,
} from "./ReviewDocumentModal";

// "On the road right now", same set as the backend's live board
// (adminOps.service.ts LIVE_TRIP_STATUSES).
const LIVE_TRIP_STATUSES: TripStatus[] = [
  TripStatus.SEARCHING,
  TripStatus.ACCEPTED,
  TripStatus.DRIVER_ARRIVED,
  TripStatus.IN_PROGRESS,
  TripStatus.INTERMEDIATE_STOP_ARRIVED,
  TripStatus.INTERMEDIATE_STOP_SKIPPED,
  TripStatus.DESTINATION_ARRIVED,
];
// "Driving to/at pickup" — the rest of LIVE_TRIP_STATUSES means passenger aboard.
const ASSIGNED_TRIP_STATUSES: TripStatus[] = [TripStatus.ACCEPTED, TripStatus.DRIVER_ARRIVED];

type LiveDriverState = "offline" | "online" | "assigned" | "onTrip";
const LIVE_STATE_BADGE: Record<LiveDriverState, { color: string; label: string }> = {
  offline: { color: "gray", label: "Offline" },
  online: { color: "green", label: "Online" },
  assigned: { color: "orange", label: "Assigned" },
  onTrip: { color: "steel", label: "On trip" },
};

function Field({ label, value }: { label: string; value?: string | number }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
        {label}
      </Text>
      <Text size="sm">{value || "—"}</Text>
    </Stack>
  );
}

export function DriverDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canSeeFinance = usePermission("finance.view");
  const canSeeAnalytics = usePermission("analytics.view");
  const canSeeReferrals = usePermission("referrals.view");
  const [approveOpen, setApproveOpen] = useState(false);
  const [licenseExpiryOpen, setLicenseExpiryOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [statusModal, setStatusModal] = useState<DriverAccountStatus | null>(
    null,
  );
  const [rejectTarget, setRejectTarget] =
    useState<ReviewDocumentTarget | null>(null);
  const [statsPeriod, setStatsPeriod] = useState<
    "day" | "week" | "month" | "custom"
  >("week");
  // Mantine date-range value: "YYYY-MM-DD" strings, matching WindowPicker's convention.
  const [customRange, setCustomRange] = useState<[string | null, string | null]>([
    null,
    null,
  ]);

  const { data: driver, isLoading, error, refetch } = useQuery({
    queryKey: ["driver", id],
    queryFn: () => driversApi.get(id),
    enabled: !!id,
    refetchInterval: 15_000,
  });

  // Ride types power the approval dropdown (assigns vehicleCategoryID).
  const { data: rideTypes } = useQuery({
    queryKey: ["ride-types", "active"],
    queryFn: () => rideTypesApi.list({ isActive: true }),
  });

  // Trips by this driver — used for the Trips, Reviews, and Stats tabs, and
  // to find their active trip for the live-status card. Polled so the card
  // stays current while this page is open.
  const driverTrips = useQuery({
    queryKey: ["driver-trips", id],
    queryFn: () => tripsApi.list({ driverID: id, limit: 50 }),
    enabled: !!id,
    refetchInterval: 15_000,
  });

  // Online hours / acceptance / cancellation are windowed (day/week/month, or
  // a custom range); reuses the fleet-wide driver-analytics aggregation,
  // scoped to this driver.
  const statsRange =
    statsPeriod === "custom"
      ? customRange[0] && customRange[1]
        ? { from: dayjs(customRange[0]).startOf("day"), to: dayjs(customRange[1]).endOf("day") }
        : null
      : {
          from: dayjs().subtract(
            { day: 1, week: 7, month: 30 }[statsPeriod],
            "day",
          ),
          to: dayjs(),
        };
  const driverPerf = useQuery({
    queryKey: [
      "driver-performance",
      id,
      statsPeriod,
      statsRange?.from.toISOString(),
      statsRange?.to.toISOString(),
    ],
    queryFn: () =>
      consoleApi.drivers({
        from: statsRange!.from.toISOString(),
        to: statsRange!.to.toISOString(),
        driverId: id,
        limit: 1,
      }),
    enabled: !!id && canSeeAnalytics && !!statsRange,
  });
  const perf = driverPerf.data?.drivers?.[0];

  // Last completed trip — all-time, independent of the stats period filter.
  const lastCompletedTrip = useQuery({
    queryKey: ["driver-last-completed-trip", id],
    queryFn: () =>
      tripsApi.list({ driverID: id, status: "completed", limit: 1 }),
    enabled: !!id,
  });
  const lastCompleted = lastCompletedTrip.data?.items?.[0];

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["driver", id] });
    void qc.invalidateQueries({ queryKey: ["drivers"] });
  }

  const approveMutation = useMutation({
    mutationFn: (vars: {
      vehicleCategoryID: string;
      reason?: string;
      licenseExpiryDate: string;
      latra: LatraApprovalDetails;
    }) =>
      driversApi.approve(
        id,
        vars.vehicleCategoryID,
        vars.reason,
        vars.licenseExpiryDate,
        vars.latra,
      ),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Driver approved" });
      setApproveOpen(false);
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const reviewDocMutation = useMutation({
    mutationFn: (vars: {
      docType: DriverDocumentType;
      status: DriverDocumentStatus;
      rejectionReason?: DocumentRejectionReason;
      rejectionComment?: string;
      licenseExpiryDate?: string;
    }) =>
      driversApi.reviewDocument(id, vars.docType, {
        status: vars.status,
        rejectionReason: vars.rejectionReason,
        rejectionComment: vars.rejectionComment,
        licenseExpiryDate: vars.licenseExpiryDate,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Document reviewed" });
      setRejectTarget(null);
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  // Approving a licence needs an expiry date, so that one routes through a
  // prompt instead of firing straight away.
  const approveDoc = (docType: DriverDocumentType) => {
    if (
      docType === "licenseDetails" &&
      !driver?.documents?.licenseDetails?.expiryDate
    ) {
      setLicenseExpiryOpen(true);
      return;
    }
    reviewDocMutation.mutate({ docType, status: "approved" });
  };

  const openReject = (docType: DriverDocumentType, label: string) =>
    setRejectTarget({ docType, label });

  const statusMutation = useMutation({
    mutationFn: (vars: { status: DriverAccountStatus; reason?: string }) =>
      driversApi.setAccountStatus(id, vars.status, vars.reason),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Status updated" });
      setStatusModal(null);
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const editMutation = useMutation({
    mutationFn: (values: DriverEditValues) =>
      driversApi.update(id, toDriverUpdateBody(values)),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Driver updated" });
      setEditOpen(false);
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  if (isLoading) {
    return (
      <Stack>
        <Skeleton h={40} w={240} />
        <Skeleton h={200} />
        <Skeleton h={200} />
      </Stack>
    );
  }

  if (error || !driver) {
    return <QueryError error={error ?? new Error("Not found")} onRetry={refetch} />;
  }

  const p = driver.personalDetails ?? {};
  const v = driver.vehicleDetails ?? {};
  const rideCategory = rideTypes?.items.find((rt) => rt._id === v.vehicleCategoryID);
  const docs = driver.documents ?? {};
  const currentStatus = driver.accountStatusDetails?.currentStatus;
  const isPending =
    currentStatus === "underApproval" || currentStatus === "pending";
  // Allowed status transitions surfaced as action buttons. A rejected driver can
  // be re-approved (re-assign vehicle category via ApproveDriverModal); a
  // suspended driver can be reactivated back to approved.
  const canApprove = isPending || currentStatus === "rejected";
  const canReactivate = currentStatus === "suspended";
  const canSuspend = currentStatus === "approved";
  const canReject = currentStatus !== "rejected" && currentStatus !== "deleted";

  // Derived from the loaded trips (Trips / Reviews tabs).
  const trips = driverTrips.data?.items ?? [];

  // Live status card: mirrors the ops-console live board's split of "under
  // trip" into assigned (driving to/at pickup) vs on-trip (passenger
  // aboard), from this driver's own trip list instead of a second endpoint.
  const activeTrip = trips.find((t) => LIVE_TRIP_STATUSES.includes(t.tripStatus as TripStatus));
  const liveState: LiveDriverState =
    driver.tripAvailabilityStatus === "offline"
      ? "offline"
      : !activeTrip
        ? "online"
        : ASSIGNED_TRIP_STATUSES.includes(activeTrip.tripStatus as TripStatus)
          ? "assigned"
          : "onTrip";
  const liveBadge = LIVE_STATE_BADGE[liveState];

  const reviews = trips
    .filter((t) => t.passengerRatingForDriver?.rating)
    .map((t) => ({
      _id: t._id,
      rating: t.passengerRatingForDriver!.rating ?? 0,
      message: t.passengerRatingForDriver!.message,
      at: t.passengerRatingForDriver!.ratedAt ?? t.updatedAt,
      rider: t.passengerDetail?.name,
    }));
  const totalTrips = driver.lifeTimeStats?.totalTrip ?? driverTrips.data?.total ?? trips.length;

  return (
    <>
      <Button
        variant="subtle"
        color="gray"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate("/drivers")}
        mb="sm"
        pl={0}
      >
        Back to drivers
      </Button>

      <PageHeader
        title={p.name ?? "Driver"}
        subtitle={p.phoneNumber}
        actions={
          <Group gap="sm">
            <Button
              variant="light"
              leftSection={<IconEdit size={16} />}
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
            {canApprove && (
              <Button
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={() => setApproveOpen(true)}
              >
                {currentStatus === "rejected" ? "Review & re-approve" : "Review & approve"}
              </Button>
            )}
            {canReactivate && (
              <Button
                color="green"
                leftSection={<IconPlayerPlay size={16} />}
                onClick={() => setStatusModal("approved")}
              >
                Reactivate
              </Button>
            )}
            {canSuspend && (
              <Button
                variant="light"
                color="orange"
                leftSection={<IconPlayerPause size={16} />}
                onClick={() => setStatusModal("suspended")}
              >
                Suspend
              </Button>
            )}
            {canReject && (
              <Button
                variant="light"
                color="red"
                leftSection={<IconBan size={16} />}
                onClick={() => setStatusModal("rejected")}
              >
                Reject
              </Button>
            )}
          </Group>
        }
      />

      <Tabs defaultValue="overview" mt="md">
        <Tabs.List>
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="documents">Documents</Tabs.Tab>
          <Tabs.Tab value="trips">Trips</Tabs.Tab>
          <Tabs.Tab value="reviews">Reviews &amp; ratings</Tabs.Tab>
          {canSeeFinance && <Tabs.Tab value="finance">Finance</Tabs.Tab>}
          {canSeeReferrals && <Tabs.Tab value="referrals">Referrals</Tabs.Tab>}
          <Tabs.Tab value="stats">Stats</Tabs.Tab>
        </Tabs.List>

        {canSeeReferrals && (
          <Tabs.Panel value="referrals" pt="md">
            <DriverReferralsTab driverId={id} />
          </Tabs.Panel>
        )}

        {canSeeFinance && (
          <Tabs.Panel value="finance" pt="md">
            <DriverFinanceTab
              driverId={id}
              currentOverride={driver.financialDetails?.maxDriverDebtOverride}
            />
          </Tabs.Panel>
        )}

        <Tabs.Panel value="overview" pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 8 }}>
          <AccountTagsCard subject="driver" accountId={id} />
          <Space h="md" />
          <Card withBorder radius="md" padding="lg" mb="md">
            <Group justify="space-between" mb={activeTrip ? "md" : 0} wrap="wrap">
              <Group gap="xs">
                <Text fw={600}>Live status</Text>
                <Badge color={liveBadge.color} variant="light" tt="none">
                  {liveBadge.label}
                </Badge>
              </Group>
              {activeTrip && (
                <Anchor component={Link} to={`/trips/${activeTrip._id}`} size="sm">
                  Open full trip
                </Anchor>
              )}
            </Group>
            {activeTrip && (
              <Stack gap={4}>
                <Group justify="space-between" wrap="nowrap">
                  <Text size="sm">
                    {activeTrip.initialTripRoute?.[0]?.name ?? "—"} →{" "}
                    {activeTrip.initialTripRoute?.[activeTrip.initialTripRoute.length - 1]?.name ?? "—"}
                  </Text>
                  <StatusBadge status={activeTrip.tripStatus} />
                </Group>
                <Text size="xs" c="dimmed">
                  Rider: {activeTrip.passengerDetail?.name ?? "—"}
                  {activeTrip.passengerDetail?.phoneNumber ? (
                    <>
                      {" · "}
                      <Anchor href={`tel:${activeTrip.passengerDetail.phoneNumber}`} size="xs">
                        {activeTrip.passengerDetail.phoneNumber}
                      </Anchor>
                    </>
                  ) : null}
                </Text>
                <Text size="xs" c="dimmed">
                  {activeTrip.rideTypeDetail?.info?.name?.en ?? ""} ·{" "}
                  {formatMoney(
                    activeTrip.paymentDetail?.pricingDetail?.netFare ??
                      activeTrip.rideTypeDetail?.rideFareDetails?.netFare,
                  )}
                </Text>
              </Stack>
            )}
          </Card>

          <Card withBorder radius="md" padding="lg" mb="md">
            <Group mb="md">
              <S3Avatar src={p.profilePicUrl} size={64} radius="md" alt={p.name}>
                {p.name?.charAt(0)?.toUpperCase() ?? "D"}
              </S3Avatar>
              <Stack gap={4}>
                <Group gap="xs">
                  <Title order={4}>{p.name ?? "—"}</Title>
                  <StatusBadge status={currentStatus} />
                </Group>
                {driver.ratingDetails?.currentRating ? (
                  <Rating
                    value={driver.ratingDetails.currentRating}
                    fractions={2}
                    readOnly
                    size="sm"
                  />
                ) : (
                  <Text size="xs" c="dimmed">
                    No ratings yet
                  </Text>
                )}
              </Stack>
            </Group>

            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
              <Field label="Email" value={p.email} />
              <Field label="Phone" value={p.phoneNumber} />
              <Field label="Gender" value={p.gender} />
              <Field label="Date of birth" value={p.dob} />
              <Field label="Availability" value={humanize(driver.tripAvailabilityStatus)} />
              <Field label="Joined" value={formatDate(driver.createdAt)} />
            </SimpleGrid>
          </Card>

          <Card withBorder radius="md" padding="lg" mb="md">
            <Title order={5} mb="md">
              Vehicle
            </Title>
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
              <Field label="Make" value={v.make} />
              <Field label="Model" value={v.model} />
              <Field label="Color" value={v.color} />
              <Field
                label="License Plate Number"
                value={v.registrationNumber}
              />
              <Field label="LATRA type" value={v.latraVehicleType} />
              <Stack gap={2}>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                  Ride category
                </Text>
                {rideCategory ? (
                  <Group gap="xs" wrap="nowrap">
                    <VehicleIcon rideType={en(rideCategory.name)} latraType={v.latraVehicleType} />
                    <Text size="sm" fw={600}>
                      {en(rideCategory.name)}
                    </Text>
                  </Group>
                ) : (
                  <Text size="sm">{v.vehicleCategoryID ? "Unknown category" : "Not assigned"}</Text>
                )}
              </Stack>
            </SimpleGrid>
            {!!v.vehicleImages?.length && (
              <SimpleGrid cols={{ base: 2, sm: 4 }} mt="md">
                {v.vehicleImages.map((src, i) => (
                  <ImageThumb key={i} src={src} label={`Vehicle ${i + 1}`} />
                ))}
              </SimpleGrid>
            )}
          </Card>

            </Grid.Col>

            {/* Status history */}
            <Grid.Col span={{ base: 12, md: 4 }}>
          <Card withBorder radius="md" padding="lg">
            <Title order={5} mb="md">
              Status history
            </Title>
            {driver.accountStatusDetails?.history?.length ? (
              <Timeline active={-1} bulletSize={18} lineWidth={2}>
                {driver.accountStatusDetails.history
                  .slice()
                  .reverse()
                  .map((h, i) => (
                    <Timeline.Item key={i} title={<StatusBadge status={h.status} />}>
                      {h.reason && (
                        <Text size="sm" c="dimmed">
                          {h.reason}
                        </Text>
                      )}
                      <Text size="xs" c="dimmed" mt={2}>
                        {formatDateTime(h.updatedAt)}
                      </Text>
                    </Timeline.Item>
                  ))}
              </Timeline>
            ) : (
              <Text size="sm" c="dimmed">
                No history recorded.
              </Text>
            )}
          </Card>
        </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="documents" pt="md">
          <Card withBorder radius="md" padding="lg">
            <Title order={5} mb="md">
              KYC Documents
            </Title>
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
              <DocCard
                label="License"
                docType="licenseDetails"
                doc={docs.licenseDetails}
                src={docs.licenseDetails?.frontImageUrl}
                expiry={docs.licenseDetails?.expiryDate}
                number={docs.licenseDetails?.licenseNumber}
                onApprove={approveDoc}
                onReject={openReject}
                busy={reviewDocMutation.isPending}
              />
              <DocCard
                label="LATRA D4 permit"
                docType="latraVehicleLicenseD4Details"
                doc={docs.latraVehicleLicenseD4Details}
                src={docs.latraVehicleLicenseD4Details?.imageUrl}
                expiry={docs.latraVehicleLicenseD4Details?.expiryDate}
                onApprove={approveDoc}
                onReject={openReject}
                busy={reviewDocMutation.isPending}
              />
              <DocCard
                label="Insurance"
                docType="vehicleInsuranceDetails"
                doc={docs.vehicleInsuranceDetails}
                src={docs.vehicleInsuranceDetails?.imageUrl}
                expiry={docs.vehicleInsuranceDetails?.expiryDate}
                onApprove={approveDoc}
                onReject={openReject}
                busy={reviewDocMutation.isPending}
              />
            </SimpleGrid>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="trips" pt="md">
          <Card withBorder radius="md" padding="lg">
            <Title order={5} mb="md">
              Trips ({totalTrips})
            </Title>
            {driverTrips.isLoading ? (
              <Text size="sm" c="dimmed">Loading…</Text>
            ) : trips.length ? (
              <Stack gap={4}>
                {trips.map((t) => (
                  <Group key={t._id} justify="space-between" wrap="nowrap" onClick={() => navigate(`/trips/${t._id}`)} style={{ cursor: "pointer" }} py={4}>
                    <div>
                      <Text size="sm" fw={500}>{t.passengerDetail?.name ?? "—"}</Text>
                      <Text size="xs" c="dimmed">{formatDateTime(t.createdAt)}</Text>
                    </div>
                    <Group gap="md" wrap="nowrap">
                      <StatusBadge status={t.tripStatus} />
                      <Text size="sm" fw={600}>{formatMoney(t.rideTypeDetail?.rideFareDetails?.netFare)}</Text>
                    </Group>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">No trips yet.</Text>
            )}
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="reviews" pt="md">
          <Card withBorder radius="md" padding="lg">
            <Group mb="md" gap="xs">
              <Title order={5}>Reviews &amp; ratings</Title>
              {driver.ratingDetails?.currentRating ? (
                <Group gap={6}>
                  <Rating value={driver.ratingDetails.currentRating} fractions={2} readOnly size="sm" />
                  <Text size="sm" c="dimmed">{driver.ratingDetails.currentRating.toFixed(1)}</Text>
                </Group>
              ) : null}
            </Group>
            {reviews.length ? (
              <Stack gap="sm">
                {reviews.map((r) => (
                  <div key={r._id}>
                    <Group gap="xs">
                      <Rating value={r.rating} readOnly size="xs" />
                      <Text size="xs" c="dimmed">
                        {formatDateTime(r.at)}{r.rider ? ` · ${r.rider}` : ""}
                      </Text>
                    </Group>
                    {r.message && <Text size="sm">“{r.message}”</Text>}
                  </div>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">No reviews yet.</Text>
            )}
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="stats" pt="md">
          <Stack gap="lg">
            <Card withBorder radius="md" padding="lg">
              <Title order={5} mb="md">
                Lifetime
              </Title>
              <SimpleGrid cols={{ base: 2, md: 4 }}>
                <StatCard
                  label="Lifetime completed trips"
                  value={String(driver.lifeTimeStats?.totalTrip ?? totalTrips)}
                />
                <StatCard
                  label="Lifetime earnings"
                  value={formatMoney(driver.lifeTimeStats?.totalEarning)}
                />
                <StatCard
                  label="Last completed trip date"
                  value={
                    lastCompletedTrip.isLoading
                      ? "…"
                      : lastCompleted
                        ? formatDateTime(lastCompleted.updatedAt)
                        : "—"
                  }
                />
                <StatCard
                  label="Last completed trip ID"
                  value={
                    lastCompletedTrip.isLoading
                      ? "…"
                      : (lastCompleted?._id ?? "—")
                  }
                  onClick={
                    lastCompleted
                      ? () => navigate(`/trips/${lastCompleted._id}`)
                      : undefined
                  }
                />
              </SimpleGrid>
            </Card>

            {canSeeAnalytics && (
              <Card withBorder radius="md" padding="lg">
                <Group justify="space-between" mb="md" wrap="wrap">
                  <Title order={5}>Performance</Title>
                  <Group gap="sm" wrap="wrap">
                    <SegmentedControl
                      size="xs"
                      value={statsPeriod}
                      onChange={(v) =>
                        setStatsPeriod(v as "day" | "week" | "month" | "custom")
                      }
                      data={[
                        { label: "Day", value: "day" },
                        { label: "Week", value: "week" },
                        { label: "Month", value: "month" },
                        { label: "Custom", value: "custom" },
                      ]}
                    />
                    {statsPeriod === "custom" && (
                      <DatePickerInput
                        type="range"
                        size="xs"
                        placeholder="Pick a date range"
                        value={customRange}
                        onChange={(v) => setCustomRange(v as [string | null, string | null])}
                        maxDate={dayjs().format("YYYY-MM-DD")}
                        w={220}
                      />
                    )}
                  </Group>
                </Group>
                {statsPeriod === "custom" && !statsRange && (
                  <Text size="xs" c="dimmed" mb="sm">
                    Pick a start and end date to see this driver's stats for that range.
                  </Text>
                )}
                <KpiGroup cols={{ base: 1, sm: 3 }}>
                  <KpiCard
                    label="Online hours"
                    value={perf?.onlineHours != null ? `${perf.onlineHours.toFixed(1)} h` : "—"}
                    hint="Time online (available or on a trip) in the selected period."
                    loading={driverPerf.isLoading}
                  />
                  <KpiCard
                    label="Acceptance rate"
                    value={formatPercent(perf?.acceptanceRate)}
                    hint="Accepted ride offers ÷ all offers sent, in the selected period."
                    loading={driverPerf.isLoading}
                  />
                  <KpiCard
                    label="Cancellation rate"
                    value={formatPercent(perf?.cancelRate)}
                    hint="Trips the driver cancelled ÷ trips assigned to them, in the selected period."
                    loading={driverPerf.isLoading}
                  />
                </KpiGroup>
              </Card>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <LicenseExpiryModal
        opened={licenseExpiryOpen}
        onClose={() => setLicenseExpiryOpen(false)}
        loading={reviewDocMutation.isPending}
        onConfirm={(licenseExpiryDate) => {
          setLicenseExpiryOpen(false);
          reviewDocMutation.mutate({
            docType: "licenseDetails",
            status: "approved",
            licenseExpiryDate,
          });
        }}
      />

      <ApproveDriverModal
        opened={approveOpen}
        onClose={() => setApproveOpen(false)}
        driverName={p.name}
        documents={docs}
        rideTypes={rideTypes?.items ?? []}
        loading={approveMutation.isPending}
        reviewing={reviewDocMutation.isPending}
        storedLicenseExpiry={docs.licenseDetails?.expiryDate}
        storedLatra={{
          registrationNumber: driver.vehicleDetails?.registrationNumber,
          licenseNumber: docs.licenseDetails?.licenseNumber,
        }}
        onApproveDocument={(docType, licenseExpiryDate) =>
          reviewDocMutation.mutate({
            docType,
            status: "approved",
            licenseExpiryDate,
          })
        }
        onRejectDocument={openReject}
        onConfirm={(vehicleCategoryID, reason, licenseExpiryDate, latra) =>
          approveMutation.mutate({
            vehicleCategoryID,
            reason,
            licenseExpiryDate,
            latra,
          })
        }
      />

      <StatusChangeModal
        status={statusModal}
        onClose={() => setStatusModal(null)}
        loading={statusMutation.isPending}
        onConfirm={(status, reason) => statusMutation.mutate({ status, reason })}
      />

      <DriverEditModal
        opened={editOpen}
        onClose={() => setEditOpen(false)}
        driver={driver}
        loading={editMutation.isPending}
        onSubmit={(values) => editMutation.mutate(values)}
      />

      <ReviewDocumentModal
        target={rejectTarget}
        onClose={() => setRejectTarget(null)}
        loading={reviewDocMutation.isPending}
        onSubmit={(vars) =>
          reviewDocMutation.mutate({ ...vars, status: "rejected" })
        }
      />
    </>
  );
}

function StatCard({
  label,
  value,
  onClick,
}: {
  label: string;
  value: string;
  onClick?: () => void;
}) {
  return (
    <Card
      withBorder
      radius="md"
      padding="lg"
      onClick={onClick}
      style={onClick ? { cursor: "pointer" } : undefined}
    >
      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
        {label}
      </Text>
      <Text fz={onClick ? 16 : 24} fw={800} mt={4} truncate="end">
        {value}
      </Text>
    </Card>
  );
}

const DOC_STATUS_COLORS: Record<DriverDocumentStatus, string> = {
  pending: "gray",
  underReview: "blue",
  approved: "green",
  rejected: "red",
};

const REJECTION_LABELS: Record<DocumentRejectionReason, string> = {
  invalidDocument: "Invalid document",
  wrongSlot: "Uploaded on the wrong slot",
  expiredDocument: "Expired document",
  fraudulentDocument: "Fraudulent document",
  vehicleMismatch: "Vehicle mismatch",
  other: "Other",
};

function DocCard({
  label,
  doc,
  src,
  expiry,
  number,
  docType,
  onApprove,
  onReject,
  busy,
}: {
  label: string;
  doc?: DocDetail;
  src?: string;
  expiry?: string;
  number?: string;
  docType?: DriverDocumentType;
  onApprove?: (docType: DriverDocumentType) => void;
  onReject?: (docType: DriverDocumentType, label: string) => void;
  busy?: boolean;
}) {
  // expiry is "DD-MM-YYYY"; parse it explicitly (Date.parse mis-handles it).
  const expiryDate = parseBackendDate(expiry);
  const expired = expiryDate ? expiryDate < new Date() : false;
  // Documents uploaded before per-document review shipped have no status.
  const status = doc?.status ?? "pending";
  const submitted = Boolean(src);

  return (
    <Box>
      <ImageThumb src={src} label={label} />

      <Group gap={4} mt={6}>
        <Badge
          size="xs"
          variant="light"
          color={submitted ? DOC_STATUS_COLORS[status] : "gray"}
        >
          {submitted ? humanize(status) : "not submitted"}
        </Badge>
        {expired && (
          <Badge size="xs" color="red" variant="light">
            expired
          </Badge>
        )}
      </Group>

      {number && (
        <Text size="xs" c="dimmed" mt={4}>
          No: {number}
        </Text>
      )}
      {expiry && (
        <Text size="xs" c="dimmed" mt={2}>
          Exp: {expiry}
        </Text>
      )}

      {status === "rejected" && doc?.rejectionReason && (
        <Text size="xs" c="var(--error-fg)" mt={4}>
          {REJECTION_LABELS[doc.rejectionReason]}
          {doc.rejectionComment ? ` — ${doc.rejectionComment}` : ""}
        </Text>
      )}

      {doc?.reviewedAt && (
        <Text size="xs" c="dimmed" mt={2}>
          Reviewed {formatDateTime(doc.reviewedAt)}
        </Text>
      )}

      {docType && submitted && (
        <Group gap={6} mt={8}>
          <Button
            size="compact-xs"
            variant="light"
            color="green"
            loading={busy}
            disabled={status === "approved"}
            onClick={() => onApprove?.(docType)}
          >
            Approve
          </Button>
          <Button
            size="compact-xs"
            variant="light"
            color="red"
            disabled={busy}
            onClick={() => onReject?.(docType, label)}
          >
            Reject
          </Button>
        </Group>
      )}
    </Box>
  );
}
